import { db } from '../firebase';
import { collection, addDoc, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import logger from '../utils/logger';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { finesService, officeError } from './finesService';

const logsCollection = collection(db, 'user_logs');

// With the new store (USE_CONTRACTS_V2) the activity log is the engine's (POST / GET /api/activity, PostgreSQL):
// a line carries only what happened - the engine stamps WHO (the signed-in user it verified from the token, and
// the role it knows) and WHEN; the list answers the same rows {id, timestamp, action_type, details, user_email,
// user_uid, user_role, metadata}, newest first - read by the Admin role only (Administration > Activity log, 20 Oct 2026).

export const adminService = {
    /**
     * Logs a user action to the 'user_logs' collection.
     * 
     * @param {string} action - The type of action (e.g., 'LOGIN', 'UPDATE_CONTRACT', 'UPLOAD_FILE').
     * @param {string} details - A human-readable description of the action.
     * @param {object} user - The user object (must contain email and preferably role).
     * @param {object} metadata - Additional machine-readable data (e.g., contractId, fileType).
     */
    logUserAction: async (action, details, user, metadata = {}) => {
        try {
            if (!user || (!user.email && !user.uid)) {
                logger.warn('Attempted to log action without valid user identifiers:', action);
                return;
            }

            if (USE_CONTRACTS_V2) {
                await finesService.activityAdd({ action, details, metadata });
                return;
            }

            const now = new Date().toISOString();
            const logEntry = {
                timestamp: now,
                // the rules ask every write for last_updated (isValidData): without it every entry was refused and
                // nothing was logged (found 4 Oct 2026)
                last_updated: now,
                action_type: action,
                details: details,
                user_email: user.email || 'N/A',
                user_uid: user.uid || 'N/A',
                user_role: user.role || 'unknown',
                metadata: metadata
            };

            await addDoc(logsCollection, logEntry);
            // logger.debug(`📝 Logged Action: [${action}] by ${user.email}`);
        } catch (error) {
            logger.error('Failed to log user action:', error);
            // We do not throw here to avoid disrupting the user flow if logging fails
        }
    },

    /**
     * Administration > Activity log (the Admin role only - the engine refuses anyone else): one page of lines, newest
     * first, and `next` - the cursor of the page after it (null at the end).
     *
     * @param {Object} filters - { userEmail, kind ('changes' | 'failed' | 'pages' | 'sign-in'), q (text), startDate, endDate }
     * @param {number} [limitCount=100]
     * @param {string} [before] - the `next` of the page before
     */
    getLogPage: async (filters = {}, limitCount = 100, before = null) => {
        if (!USE_CONTRACTS_V2) {
            return { items: await adminService.getLogs(filters, limitCount), next: null };
        }
        const answer = await finesService.activityList({
            user_email: filters.userEmail || undefined,
            kind: filters.kind || undefined,
            q: (filters.q || '').trim() || undefined,
            start: filters.startDate ? filters.startDate.toISOString() : undefined,
            end: filters.endDate ? filters.endDate.toISOString() : undefined,
            before: before || undefined,
            limit: Math.min(Math.max(1, Math.floor(Number(limitCount) || 100)), 1000),
        }).catch((e) => { throw officeError(e); });
        return { items: answer?.items || [], next: answer?.next || null };
    },

    /**
     * Fetches user activity logs with optional filters.
     * 
     * @param {Object} filters - Filter criteria
     * @param {Date} [filters.startDate] - Start date for filtering
     * @param {Date} [filters.endDate] - End date for filtering
     * @param {string} [filters.userEmail] - Filter by specific user email
     * @param {string} [filters.actionType] - Filter by action type
     * @param {number} [limitCount=50] - Max number of logs to return
     */
    getLogs: async (filters = {}, limitCount = 50) => {
        if (USE_CONTRACTS_V2) {
            // a refused / failed read throws (permission-denied for a 403): never shown as "no activity"
            const answer = await finesService.activityList({
                user_email: filters.userEmail || undefined,
                action: filters.actionType || undefined,
                start: filters.startDate ? filters.startDate.toISOString() : undefined,
                end: filters.endDate ? filters.endDate.toISOString() : undefined,
                limit: Math.min(Math.max(1, Math.floor(Number(limitCount) || 50)), 1000),
            }).catch((e) => { throw officeError(e); });
            return answer?.items || [];
        }
        const buildQuery = (applyUserFilter) => {
            const constraints = [orderBy('timestamp', 'desc'), limit(limitCount)];

            if (applyUserFilter && filters.userEmail) {
                constraints.unshift(where('user_email', '==', filters.userEmail));
            }

            if (filters.startDate) {
                constraints.push(where('timestamp', '>=', filters.startDate.toISOString()));
            }
            if (filters.endDate) {
                constraints.push(where('timestamp', '<=', filters.endDate.toISOString()));
            }

            return query(logsCollection, ...constraints);
        };

        try {
            // Try standard query (requires index for userEmail + timestamp)
            const q = buildQuery(true);
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        } catch (error) {
            // Fallback: If index missing, fetch latest N and filter client-side
            if (error.code === 'failed-precondition' || error.message.includes('index')) {
                logger.warn('⚠️ Missing Index for efficient filtering. Falling back to client-side filter.');

                try {
                    // Fetch generic latest logs (increase limit to effective search space)
                    // const fallbackLimit = Math.max(limitCount * 2, 100);
                    const qFallback = buildQuery(false); // No user filter in query

                    const snapshot = await getDocs(qFallback);
                    let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                    // Apply Client-Side Filtering
                    if (filters.userEmail) {
                        results = results.filter(log => log.user_email === filters.userEmail);
                    }

                    return results;
                } catch (fallbackError) {
                    logger.error('Fallback fetch failed:', fallbackError);
                    return [];
                }
            }

            logger.error('Error fetching logs:', error);
            return [];
        }
    }
};
